import GanymedeKeyValueTable from "../../../components/ganymede/KeyValueTable";
import { useForm } from "react-hook-form";
import common from "../../../lib/common";
import { useEffect, useRef, useState } from "react";
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import SnoutProgressBtn from '../../../components/SnoutProgressBtn/SnoutProgressBtn';
import api from '../../../services/jupiter_api';
import StepContainer from "../StepContainer";
import { track } from '@amplitude/analytics-browser';
import { AMPLITUDE_EVENT_CODES } from '../../../constants';
import GanymedeInput from '../../../components/ganymede/GanymedeInput';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../../lib/stripe';

function CheckoutStep({ state, dispatch }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentError, setSetupIntentError] = useState(null);

  useEffect(() => {
    api.createSetupIntent()
      .then(res => setClientSecret(res.client_secret))
      .catch(() => setSetupIntentError('Failed to initialize payment. Please refresh and try again.'));
  }, []);

  if (setupIntentError) {
    return (
      <StepContainer back={() => dispatch({type: 'prev'})}>
        <div className={'font-roboto-slab text-3xl sm:text-4xl text-center mb-8'}>Billing Information</div>
        <p style={{color: '#c00', textAlign: 'center'}}>{setupIntentError}</p>
      </StepContainer>
    );
  }

  if (!clientSecret) {
    return (
      <StepContainer back={() => dispatch({type: 'prev'})}>
        <div className={'font-roboto-slab text-3xl sm:text-4xl text-center mb-8'}>Billing Information</div>
        <p style={{textAlign: 'center', color: '#828282'}}>Loading payment form...</p>
      </StepContainer>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm state={state} dispatch={dispatch} />
    </Elements>
  );
}

function CheckoutForm({ state, dispatch }) {
  const stripe = useStripe();
  const elements = useElements();

  const { toasts } = useToasterStore();
  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= 3)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  const { handleSubmit, register, formState: { errors }, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      first_name: state?.paymentMethod?.first_name || '',
      last_name: state?.paymentMethod?.last_name || '',
      tos: false,
    },
  });

  const btnRef = useRef();

  const checkout = async (paymentMethodId, formValues) => {
    try {
      const payment = {
        payment_method_id: paymentMethodId,
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        ...state.personalDetails,
      };
      const result = await api.clientSelfEnroll(state.client, state.currentPet, state.selectedPlan, payment);
      track(AMPLITUDE_EVENT_CODES.SELF_ENROLL_PAYMENT_SUCCESS, {
        vet: state.vet,
        client: state.client,
        pet: state.currentPet,
        plan: state.selectedPlan,
      });
      btnRef?.current?.success();
      setTimeout(() => {
        dispatch({ type: 'next', planInstanceId: result.external_id });
      }, 1000);

    } catch(e) {
      track(AMPLITUDE_EVENT_CODES.SELF_ENROLL_PAYMENT_FAILURE, {
        vet: state.vet,
        client: state.client,
        pet: state.currentPet,
        plan: state.selectedPlan,
        error: e,
      });
      btnRef?.current?.error();
      toast(t => {
        return (<>
          <button className={'close-toast-button'} onClick={() => toast.dismiss(t.id)}>x</button>
          {e.message}
        </>)
      }, {
        type: 'error',
        duration: Infinity,
      });
    }
  }

  const onSubmit = async (formValues) => {
    if (!stripe || !elements) return;

    btnRef?.current?.loading();

    let result;
    try {
      result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });
    } catch (err) {
      btnRef?.current?.error();
      toast(t => (<>
        <button className={'close-toast-button'} onClick={() => toast.dismiss(t.id)}>x</button>
        {err.message || 'Payment verification failed. Please try again.'}
      </>), { type: 'error', duration: Infinity });
      return;
    }

    if (result.error) {
      btnRef?.current?.error();
      toast(t => (<>
        <button className={'close-toast-button'} onClick={() => toast.dismiss(t.id)}>x</button>
        {result.error.message}
      </>), { type: 'error', duration: Infinity });
      return;
    }

    await checkout(result.setupIntent.payment_method, formValues);
  }

  const getPlanDetails = () => {
    let planDetails = {'Plan': state?.selectedPlan?.name, 'Monthly Payment': common.pennyIntToDollarString(state?.selectedPlan?.monthly_price)};
    const enrollmentFeeAmt = state?.selectedPlan?.enrollment_fee;
    if (enrollmentFeeAmt > 0) {
      planDetails['Enrollment fee'] = common.pennyIntToDollarString(enrollmentFeeAmt);
    }
    return planDetails;
  }

  return (
    <StepContainer back={() => dispatch({type: 'prev'})}>
      <div className={'font-roboto-slab text-3xl sm:text-4xl text-center mb-8'}>Billing Information</div>
      <div className={' bg-gray-50'}>
        <GanymedeKeyValueTable
            className={'total-table'}
            style={{width: '100%'}}
            data={getPlanDetails()}/>
        <GanymedeKeyValueTable
            className={'total-table last:[&_th]:text-right'}
            style={{width: '100%', marginTop: '0.63rem'}}
            header={[`Total`, `${common.pennyIntToDollarString(state?.selectedPlan?.monthly_price + state?.selectedPlan?.enrollment_fee)}`]}
        />
      </div>
      <p style={{fontSize: '0.88rem', color: '#828282'}}>Complete your care plan enrollment by providing your payment details and reviewing our membership agreement</p>
      <form className={'flex flex-col items-center'} onSubmit={handleSubmit(onSubmit)}>
        <div className={`grid grid-cols-6 gap-x-2.5 w-full mb-2`}>
          <GanymedeInput
            className={'col-span-full sm:col-span-3'}
            label="First Name"
            type="text"
            errors={errors}
            {...register("first_name", {
              required: { value: true, message: 'First name is required' }
            })}
          />
          <GanymedeInput
            className={'col-span-full sm:col-span-3'}
            label="Last Name"
            type="text"
            errors={errors}
            {...register("last_name", {
              required: { value: true, message: 'Last name is required' }
            })}
          />
          <div className={'col-span-full'}>
            <PaymentElement />
          </div>
        </div>
        <div className="mb-8">
          <div className={''}>
            <div>
              <input id="agreementCheckbox" className={'agreement-checkbox'}
                     type="checkbox"
                     {...register("tos", { required: { value: true, message: 'You must agree to our Membership Agreement to proceed' } })}
              />
              <label className={'text-left sm:text-base'} htmlFor="agreementCheckbox">
                YES, I understand that I will be charged monthly for {state?.currentPet?.name}'s Snout wellness plan and I have read and agree to be bound by the <button type={'button'} className={'bg-gray-50 text-snout-orange border-0 p-0 font-roboto text-base cursor-pointer'} onClick={() => {
                const formState = getValues();
                dispatch({ type: 'showTos', formState, })
              }}>Snout Membership Agreement</button>.
              </label>
            </div>
          </div>
          {errors?.tos && <span className={'form-validation-error'}>{errors.tos.message}</span>}
        </div>
        <div className={'w-40'}>
          <SnoutProgressBtn ref={btnRef} disabled={!stripe}>Continue</SnoutProgressBtn>
        </div>
      </form>
      <Toaster position={'bottom-center'}/>
    </StepContainer>
  );
}

export default CheckoutStep;
